/**
 * Cache-key construction.
 *
 * Goal: produce stable, collision-free string keys for the read-side cache that
 * stay namespaced per-user (so a sign-out / sign-in swap can never serve stale
 * data from the previous identity) and per-procedure (so prefix-based
 * invalidation can target a procedure family with one wildcard).
 *
 * Format:
 *   `proof.qcache.v1.<userId>.<procedure>.<canonical-input-json>`
 *
 * Versioned prefix:
 *   `proof.qcache.v1` is the schema-version namespace. If the cache shape ever
 *   changes incompatibly (e.g. payload envelope format, hash function, or
 *   per-tenant scope), bump the version and previously-stored entries become
 *   unreachable — they will eventually be evicted by IndexedDB's LRU. This is
 *   the cheapest migration policy at MVP scale.
 *
 * Canonicalisation:
 *   Two calls with the same logical input must produce the same key. JS object
 *   key order is insertion-order, so `{ limit: 20, cursor: null }` and
 *   `{ cursor: null, limit: 20 }` both serialise differently with `JSON.stringify`.
 *   We sort keys recursively before serialising. Arrays preserve order
 *   (positional-meaningful). `undefined` values are dropped (matches JSON
 *   semantics + RPC input semantics — undefined fields are absent, not "null").
 *
 * Ref: tasks/pr-body-p4-followup-3.md performance ladder Path A — read-side
 * SWR cache for hot oRPC reads.
 */

const CACHE_PREFIX = "proof.qcache.v1";
const ANON_USER = "__anon__";

export interface CacheKeyParams {
  /** Authenticated user UUID. Pass `null` for the anonymous fallback. */
  userId: string | null;
  /**
   * Procedure path on the oRPC router, e.g. `"recipe.list"`, `"recipe.get"`,
   * `"attempt.list"`, `"collection.list"`. Used as both an addressing segment
   * and the prefix for `invalidate(...)` calls.
   */
  procedure: string;
  /**
   * Procedure input. Serialised canonically (sorted keys, undefined dropped).
   * Pass `undefined` (or omit) for procedures with no input.
   */
  input?: unknown;
}

/**
 * Build a stable cache key for a read procedure.
 *
 * The key is a string (IndexedDB keys may be other types but strings are the
 * simplest interoperable choice and align with the in-memory L1 Map).
 */
export function cacheKey(params: CacheKeyParams): string {
  const userPart = params.userId ?? ANON_USER;
  const inputPart = canonicalJson(params.input);
  return `${CACHE_PREFIX}.${userPart}.${params.procedure}.${inputPart}`;
}

/**
 * Build the procedure-prefix string used by `invalidate()`. Matches every key
 * for the given procedure family on the given user, regardless of input.
 *
 * Example:
 *   procedurePrefix({ userId: "u1", procedure: "recipe" })
 *     -> "proof.qcache.v1.u1.recipe"
 *   // matches recipe.list, recipe.get, recipe.slugHistory, etc.
 */
export function procedurePrefix(params: { userId: string | null; procedure: string }): string {
  const userPart = params.userId ?? ANON_USER;
  return `${CACHE_PREFIX}.${userPart}.${params.procedure}`;
}

/**
 * Build the full prefix for a user (used by `invalidateAll()` on sign-out).
 */
export function userPrefix(userId: string | null): string {
  const userPart = userId ?? ANON_USER;
  return `${CACHE_PREFIX}.${userPart}.`;
}

/**
 * Build the global cache prefix (matches every cache entry across all users).
 * Useful for full clears.
 */
export function globalPrefix(): string {
  return `${CACHE_PREFIX}.`;
}

// ── Canonical JSON ───────────────────────────────────────────────────────────

/**
 * Stable JSON serialisation: object keys sorted, undefined dropped, non-finite
 * numbers preserved as JSON `null` (matches `JSON.stringify`). Cycles are not
 * supported — RPC inputs are flat DTOs, so this is a non-issue in practice; a
 * cycle here would surface as a stack overflow at dev time, which is the
 * preferred failure mode (loud + early).
 */
function canonicalJson(value: unknown): string {
  if (value === undefined) return "{}";
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value: unknown): unknown {
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value.map(sortKeys);
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const k of Object.keys(obj).sort()) {
      const v = obj[k];
      // Drop undefined to match RPC absent-vs-null distinction.
      if (v === undefined) continue;
      sorted[k] = sortKeys(v);
    }
    return sorted;
  }
  return value;
}
